import { useState, useEffect, FormEvent } from 'react';
import { AddressAutocomplete, AddressResult } from './AddressAutocomplete';

interface ChatInputProps {
  onSubmit: (message: string, metadata?: Record<string, unknown>) => void;
  isLoading: boolean;
  inputType: string;
  placeholder: string;
  hasButtons: boolean;
}

export function ChatInput({ onSubmit, isLoading, inputType, placeholder, hasButtons }: ChatInputProps) {
  const [value, setValue] = useState('');

  // Wechselt der Eingabetyp, darf kein Rest aus dem vorherigen Feld stehen bleiben.
  // Sonst ginge z.B. eine nie abgeschickte Frage aus „Frage stellen..." als Adresse raus.
  useEffect(() => {
    setValue('');
  }, [inputType]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (value.trim() && !isLoading) {
      onSubmit(value.trim());
      setValue('');
    }
  };

  const handleAddressSelect = (address: AddressResult) => {
    if (isLoading) return;
    // Send the full address with metadata
    onSubmit(address.fullAddress, {
      addressData: {
        street: address.street,
        houseNumber: address.houseNumber,
        postcode: address.postcode,
        city: address.city,
        state: address.state,
      },
    });
    setValue('');
  };

  // Show compact question field if there are buttons and no explicit input type
  if (hasButtons && inputType === 'none') {
    return (
      <div className="chat-input-area-compact">
        <form className="chat-input-form" onSubmit={handleSubmit}>
          <input
            type="text"
            className="chat-input chat-input-question"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Frage stellen..."
            aria-label="Frage stellen"
            disabled={isLoading}
            autoComplete="off"
          />
          <button
            type="submit"
            className="chat-submit chat-submit-small"
            disabled={isLoading || !value.trim()}
            aria-label="Frage senden"
          />
        </form>
      </div>
    );
  }

  const getInputType = () => {
    switch (inputType) {
      case 'email':
        return 'email';
      case 'tel':
        return 'tel';
      case 'number':
        return 'number';
      default:
        return 'text';
    }
  };

  // Use address autocomplete for address input. Das Formular sendet den Rohtext,
  // auch ohne Vorschlag: das Backend versteht Adressen als Freitext.
  if (inputType === 'address') {
    return (
      <div className="chat-input-area">
        <form className="chat-input-form" onSubmit={handleSubmit}>
          <AddressAutocomplete
            value={value}
            onChange={setValue}
            onSelect={handleAddressSelect}
            placeholder={placeholder || 'Adresse eingeben...'}
            disabled={isLoading}
          />
          <button
            type="submit"
            className="chat-submit"
            disabled={isLoading || !value.trim()}
            aria-label="Adresse senden"
          />
        </form>
      </div>
    );
  }

  return (
    <div className="chat-input-area">
      <form className="chat-input-form" onSubmit={handleSubmit}>
        <input
          type={getInputType()}
          className="chat-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder || 'Nachricht eingeben...'}
          aria-label={placeholder || 'Nachricht eingeben'}
          disabled={isLoading}
          autoComplete={inputType === 'email' ? 'email' : inputType === 'tel' ? 'tel' : 'off'}
        />
        <button
          type="submit"
          className="chat-submit"
          disabled={isLoading || !value.trim()}
          aria-label="Senden"
        />
      </form>
    </div>
  );
}
